import { useEffect, useState } from "react";
import api from "@/api";
import { useNoctua } from "./NoctuaContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TrendingUp, Shield, Target, AlertTriangle, Layers } from "lucide-react";

const STRATEGY_META = {
  CRECER: { icon: TrendingUp, color: "#16A34A", label: "Dónde crecer", desc: "Switcher con margen <8% o abstención >45%" },
  DEFENDER: { icon: Shield, color: "#2563EB", label: "Dónde defender", desc: "Tu partido gana con margen <10%" },
  AJUSTAR: { icon: Target, color: "#EAB308", label: "Dónde ajustar", desc: "Territorio en disputa" },
  NO_INVERTIR: { icon: AlertTriangle, color: "#DC2626", label: "Dónde no invertir", desc: "Voto duro rival con margen >20%" },
};

// Per-layer interpretive description shown above the cards.
// Helps users understand what the indicator measures and how to read the numbers.
const LAYER_INFO = {
  distribucion: {
    title: "Distribución del voto",
    formula: "Top 3 partidos por % en la última elección.",
    interpret: "Lectura rápida del reparto electoral municipal. Te dice quién manda y con qué fuerza relativa.",
    cta: "Útil para identificar municipios con voto fragmentado vs. dominado.",
  },
  switcheo: {
    title: "Volatilidad de voto (switcheo)",
    formula: "Cuenta cuántas veces cambió el ganador entre los ciclos 2018 · 2021 · 2024.",
    interpret: "MUY ALTA = 2 cambios · ALTA = 1 cambio · BAJA = 0 cambios. Los municipios con switcheo alto son los más sensibles a la oferta política.",
    cta: "Ahí es donde una campaña bien ejecutada puede mover la elección.",
  },
  rentabilidad: {
    title: "Rentabilidad electoral (0 – 100)",
    formula: "Score = 100 − (margen_ganador × 2.5). Margen 0 pts → 100. Margen 10 pts → 75. Margen 20 pts → 50. Margen ≥ 40 pts → 0.",
    interpret: "Mide el retorno político por unidad de esfuerzo invertido. Mientras menor el margen del ganador, mayor el impacto de cada voto que muevas. Un municipio con 80 pts es altamente rentable; uno con 5 pts es voto duro consolidado donde tu esfuerzo rinde poco.",
    cta: "Prioriza municipios con score alto: ahí cada peso y cada visita genera mayor desplazamiento del marcador.",
  },
  ganador: {
    title: "Ganador territorial",
    formula: "HISTÓRICO = mismo partido ganó los 3 ciclos. VARIABLE = el ganador cambió al menos una vez.",
    interpret: "Identifica plazas con lealtad estructural vs. plazas competitivas. El partido mostrado es el ganador más reciente.",
    cta: "Los HISTÓRICOS rivales son territorio caro de mover; los VARIABLES son tu objetivo táctico.",
  },
  abstencionismo: {
    title: "Abstencionismo (% de la lista nominal que no votó)",
    formula: "% = 100 − participación. Niveles: CRÍTICA >55% · ALTA 45–55% · MEDIA 35–45% · BAJA <35%. \"Voto movilizable\" = lista_nominal × abstención.",
    interpret: "Cuantifica el votante latente. Un municipio con 50% de abstención sobre 20,000 LN tiene 10,000 votos potenciales sin activar.",
    cta: "Donde la abstención es alta y tu partido pierde por poco, hay margen real para crecer movilizando, no convenciendo.",
  },
  estrategia: {
    title: "Asignación estratégica de recursos",
    formula: "Cruza ganador + margen + switcheo + abstención para clasificar cada municipio en uno de 4 cuadrantes accionables.",
    interpret: "CRECER (verde) = oportunidad real de victoria · DEFENDER (azul) = ganas pero apretado · AJUSTAR (amarillo) = disputa · NO INVERTIR (rojo) = voto duro rival.",
    cta: "Esta capa convierte los datos en un mapa de inversión de campaña.",
  },
};

const ABS_COLORS = {
  CRITICA: "#DC2626",
  ALTA: "#EA580C",
  MEDIA: "#EAB308",
  BAJA: "#16A34A",
};

export default function LecturaPage() {
  const { partidos, clientParty, selectedState, scopeFilter } = useNoctua();
  const estado = Number(selectedState);
  const [layer, setLayer] = useState("distribucion");
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/noctua/lectura/${estado}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [estado]);

  const partyColor = (k) => partidos.find((p) => p.clave === k)?.color || "#64748B";
  const isMunicipalScope = scopeFilter?.scope_type === "municipal" && scopeFilter?.municipio_clave;
  const isDistrictScope = scopeFilter?.scope_type === "distrital_federal" || scopeFilter?.scope_type === "distrital_local";
  const districtLoaded = isDistrictScope && scopeFilter?.district_mapping_loaded && (scopeFilter.district_municipios_claves?.length > 0);
  let scopedItems = data.items;
  if (isMunicipalScope) {
    scopedItems = scopedItems.filter((i) => Number(i.municipio_clave) === Number(scopeFilter.municipio_clave));
  } else if (districtLoaded) {
    const allowed = new Set(scopeFilter.district_municipios_claves.map(Number));
    scopedItems = scopedItems.filter((i) => allowed.has(Number(i.municipio_clave)));
  }
  const withData = scopedItems.filter((i) => i.has_data);

  const layers = [
    { key: "distribucion", label: "Distribución" },
    { key: "switcheo", label: "Switcheo" },
    { key: "rentabilidad", label: "Rentabilidad" },
    { key: "ganador", label: "Ganador territorial" },
    { key: "abstencionismo", label: "Abstencionismo" },
    { key: "estrategia", label: "Estrategia" },
  ];

  // Strategy groups
  const grouped = {};
  if (layer === "estrategia") {
    withData.forEach((it) => {
      grouped[it.estrategia] = grouped[it.estrategia] || [];
      grouped[it.estrategia].push(it);
    });
  }

  return (
    <div className="space-y-6" data-testid="noctua-lectura-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Análisis por capas</div>
          <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-white">Lectura Territorial</h1>
          <p className="text-sm text-slate-400 mt-1">
            5 capas + estrategia · Partido cliente: <span className="font-bold text-white">{clientParty || "—"}</span>
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
          Estado activo (cambiar arriba)
        </div>
      </div>

      {/* Layer tabs */}
      <div className="flex gap-2 flex-wrap">
        {layers.map((l) => (
          <button
            key={l.key}
            onClick={() => setLayer(l.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
              layer === l.key ? "bg-blue-600 text-white" : "bg-slate-900 text-slate-300 border border-slate-700 hover:border-slate-600"
            }`}
            data-testid={`lectura-layer-${l.key}`}
          >
            <Layers className="w-3 h-3" />
            {l.label}
          </button>
        ))}
      </div>

      {isMunicipalScope && (
        <div className="noctua-card p-3 border-l-4 border-l-emerald-500 flex items-center gap-2 text-sm text-slate-200" data-testid="lectura-municipal-banner">
          <Layers className="w-4 h-4 text-emerald-400" />
          Vista restringida al municipio{" "}
          <strong className="text-emerald-300 ml-1">{scopeFilter?.municipio_nombre || `#${scopeFilter?.municipio_clave}`}</strong>
        </div>
      )}

      {isDistrictScope && !districtLoaded && (
        <div className="noctua-card p-3 border-l-4 border-l-amber-500 flex items-start gap-2 text-sm text-slate-200" data-testid="lectura-distrital-banner">
          <Layers className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <strong className="text-amber-400">Cartografía distrital pendiente.</strong>{" "}
            Estás en un distrito {scopeFilter?.scope_type === "distrital_federal" ? "federal" : "local"} (#{scopeFilter?.distrito_clave}).
            Se muestran los municipios del estado como referencia. El Super Admin puede cargar el mapping distrito→municipios en Datos.
          </div>
        </div>
      )}

      {isDistrictScope && districtLoaded && (
        <div className="noctua-card p-3 border-l-4 border-l-emerald-500 flex items-center gap-2 text-sm text-slate-200" data-testid="lectura-distrital-loaded-banner">
          <Layers className="w-4 h-4 text-emerald-400" />
          Vista restringida al {scopeFilter?.scope_type === "distrital_federal" ? "Distrito Federal" : "Distrito Local"}{" "}
          <strong className="text-emerald-300 ml-1">#{scopeFilter?.distrito_clave}</strong>
          <span className="text-slate-400 mx-1">·</span>
          <span className="text-slate-300">{scopeFilter.district_municipios_claves.length} municipios</span>
        </div>
      )}

      {/* Interpretive description for the active layer */}
      {LAYER_INFO[layer] && (
        <div
          className="noctua-card p-4 border-l-2 border-l-blue-500"
          data-testid={`lectura-info-${layer}`}
        >
          <div className="flex items-start gap-3">
            <Layers className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Qué mide</span>
                <p className="font-display font-bold text-white text-base mt-0.5">{LAYER_INFO[layer].title}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Fórmula</span>
                <p className="text-slate-300 text-xs leading-relaxed">{LAYER_INFO[layer].formula}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Interpretación</span>
                <p className="text-slate-300 text-xs leading-relaxed">{LAYER_INFO[layer].interpret}</p>
              </div>
              <p className="text-blue-300 text-xs italic pt-1 border-t border-slate-800/60">
                {LAYER_INFO[layer].cta}
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="noctua-card p-12 text-center text-slate-400">Cargando…</div>
      ) : withData.length === 0 ? (
        <div className="noctua-card p-12 text-center">
          <p className="text-slate-400">Sin resultados cargados todavía. Importa CSV en Datos.</p>
        </div>
      ) : layer === "estrategia" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(STRATEGY_META).map(([key, meta]) => (
            <div key={key} className="noctua-card p-5">
              <div className="flex items-center gap-2">
                <meta.icon className="w-5 h-5" style={{ color: meta.color }} />
                <div className="font-display font-bold text-lg text-white">{meta.label}</div>
                <span className="ml-auto text-2xl font-display font-black tabular-nums" style={{ color: meta.color }}>
                  {(grouped[key] || []).length}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">{meta.desc}</p>
              <ul className="mt-3 space-y-1.5 max-h-44 overflow-y-auto">
                {(grouped[key] || []).slice(0, 10).map((it) => (
                  <li key={it.municipio_id} className="text-sm text-slate-200 flex items-center justify-between" data-testid={`strategy-${key}-${it.municipio_id}`}>
                    <span>{it.nombre}</span>
                    <span className="text-xs text-slate-500">{it.distribucion?.[0]?.partido} {it.distribucion?.[0]?.pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {withData.map((it) => (
            <div key={it.municipio_id} className="noctua-card noctua-card-hover p-4" data-testid={`lectura-card-${it.municipio_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-white">{it.nombre}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">
                    Año {it.anio_latest}
                  </div>
                </div>
                {layer === "switcheo" && (
                  <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider"
                    style={{
                      backgroundColor:
                        it.switcheo.label === "MUY ALTA" ? "rgba(220,38,38,0.18)" :
                        it.switcheo.label === "ALTA" ? "rgba(234,179,8,0.18)" : "rgba(22,163,74,0.18)",
                      color:
                        it.switcheo.label === "MUY ALTA" ? "#FCA5A5" :
                        it.switcheo.label === "ALTA" ? "#FCD34D" : "#86EFAC",
                    }}>
                    Volatilidad: {it.switcheo.label}
                  </span>
                )}
              </div>

              {layer === "distribucion" && (
                <div className="mt-3 space-y-2">
                  {it.distribucion.map((p) => (
                    <div key={p.partido} className="flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: partyColor(p.partido) }} />
                      <span className="w-12 text-slate-300">{p.partido}</span>
                      <div className="flex-1 h-1.5 bg-slate-800 rounded overflow-hidden">
                        <div className="h-full" style={{ width: `${p.pct}%`, backgroundColor: partyColor(p.partido) }} />
                      </div>
                      <span className="text-white w-10 text-right tabular-nums">{p.pct}%</span>
                    </div>
                  ))}
                </div>
              )}

              {layer === "switcheo" && (
                <div className="mt-3 text-xs text-slate-300">
                  Ganadores: {it.switcheo.winners.join(" → ")}
                  <div className="mt-1 text-slate-500">{it.switcheo.switches} cambios entre ciclos.</div>
                </div>
              )}

              {layer === "rentabilidad" && (
                <div className="mt-3">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-black text-3xl text-blue-400 tabular-nums">{it.rentabilidad.score}</span>
                    <span className="text-xs text-slate-400">/100</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Margen ganador: {it.rentabilidad.margen_pts} pts</div>
                  <div className="mt-2 h-1.5 bg-slate-800 rounded overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" style={{ width: `${it.rentabilidad.score}%` }} />
                  </div>
                </div>
              )}

              {layer === "ganador" && (
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-400">{it.ganador_territorial.tipo}</div>
                    <div className="font-display font-bold text-lg" style={{ color: partyColor(it.ganador_territorial.partido) }}>
                      {it.ganador_territorial.partido}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">
                    {it.switcheo.winners.length} ciclos
                  </div>
                </div>
              )}

              {layer === "abstencionismo" && (
                <div className="mt-3">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-black text-3xl tabular-nums" style={{ color: ABS_COLORS[it.abstencionismo.level] }}>
                      {it.abstencionismo.pct}%
                    </span>
                    <span className="text-xs uppercase tracking-wider" style={{ color: ABS_COLORS[it.abstencionismo.level] }}>
                      {it.abstencionismo.level}
                    </span>
                  </div>
                  {it.abstencionismo.delta !== null && (
                    <div className="text-xs text-slate-500 mt-1">
                      Δ vs ciclo anterior: <span className={it.abstencionismo.delta > 0 ? "text-red-400" : "text-green-400"}>
                        {it.abstencionismo.delta > 0 ? "+" : ""}{it.abstencionismo.delta} pts
                      </span>
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-1">
                    Voto movilizable: <span className="text-white">{it.abstencionismo.voto_movilizable?.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
